import { useCopilotAction } from '@copilotkit/react-core';

import { Deployment } from 'types/deployment';

import { useAPI } from 'hooks/api/useAPI';

export function useListDeploymentsTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listDeployments',
    description:
      'List deployments in the system. Use this to find deployment IDs, check deployment states, or see which pipelines are deployed to which gateways. Can filter by state, gateway, pipeline, or video source.',
    parameters: [
      {
        name: 'ids',
        type: 'string',
        description: 'JSON array of deployment IDs to fetch',
        required: false,
      },
      {
        name: 'states',
        type: 'string',
        description:
          'JSON array of states to filter by: ["deploying", "running", "restart_waiting", "stopping", "stopped", "error", "interrupted", "unknown"]',
        required: false,
      },
      {
        name: 'names',
        type: 'string',
        description: 'JSON array of deployment names to search for',
        required: false,
      },
      {
        name: 'pipelineIds',
        type: 'string',
        description: 'JSON array of pipeline IDs to filter deployments by',
        required: false,
      },
      {
        name: 'gatewayIds',
        type: 'string',
        description: 'JSON array of gateway IDs to filter deployments by',
        required: false,
      },
      {
        name: 'videoSourceIds',
        type: 'string',
        description: 'JSON array of video source IDs to filter deployments by',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      ids?: string | string[];
      states?: string | string[];
      names?: string | string[];
      pipelineIds?: string | string[];
      gatewayIds?: string | string[];
      videoSourceIds?: string | string[];
      limit?: number;
    }) => {
      const parseArrayParam = (param: string | string[] | undefined): string[] | undefined => {
        if (!param) return undefined;
        if (Array.isArray(param)) return param;
        try {
          return JSON.parse(param);
        } catch {
          // If it's not valid JSON, treat it as a single value
          return [param];
        }
      };

      try {
        const ids = parseArrayParam(args.ids);
        const states = parseArrayParam(args.states);
        const names = parseArrayParam(args.names);
        const pipelineIds = parseArrayParam(args.pipelineIds);
        const gatewayIds = parseArrayParam(args.gatewayIds);
        const videoSourceIds = parseArrayParam(args.videoSourceIds);

        const { data } = await client.get(
          `/apps/${applicationID}/deployments`,
          {
            params: {
              pagination: 'offset',
              page: 1,
              limit: args.limit || 50,
              deployment_ids: ids,
              states,
              deployment_names: names,
              pipeline_ids: pipelineIds,
              gateway_ids: gatewayIds,
              video_source_ids: videoSourceIds,
            },
          }
        );

        const deployments = data?.data || [];

        if (deployments.length === 0) {
          return {
            message: 'No deployments found matching your criteria.',
            deployments: [],
          };
        }

        return {
          message: `Found ${deployments.length} deployment(s)`,
          deployments: deployments.map((deployment: Deployment) => deployment),
        };
      } catch (error) {
        return {
          message: 'Failed to load deployments',
          error: error instanceof Error ? error.message : 'Unknown error',
          deployments: [],
        };
      }
    },
  });
}
